import type { BaseClient } from '../../http/BaseClient.js';
import type {
  Deployment,
  RegisterDeploymentRequest,
  RegisterDeploymentResponse,
  DeploymentQuota,
  SetDeploymentQuotaRequest,
} from './types.js';

// Org-scoped private/sovereign deployment management. Backend routes hang off
// the root authed group — see internal/http/routes/federation (RegisterPortal)
// in cloud-agent-backend. The matching machine edge (/federation/enroll,
// /heartbeat) is driven by the appliance itself, not this SDK.
const BASE = '/deployments';

export class DeploymentsClient {
  constructor(private readonly http: BaseClient) {}

  /** List the org's deployments, newest first. */
  list(): Promise<{ deployments: Deployment[] }> {
    return this.http.get(BASE);
  }

  /** Fetch one deployment by id. */
  get(id: string): Promise<{ deployment: Deployment }> {
    return this.http.get(`${BASE}/${encodeURIComponent(id)}`);
  }

  /**
   * Register a new deployment: reserves a quota slot and mints a one-time
   * enrollment voucher. The voucher + install command in the response are shown
   * ONCE (only the voucher hash is stored server-side).
   */
  register(body: RegisterDeploymentRequest): Promise<RegisterDeploymentResponse> {
    return this.http.post(BASE, { body });
  }

  /** Decommission a deployment, freeing its quota slot. */
  decommission(id: string): Promise<{ success: boolean }> {
    return this.http.post(`${BASE}/${encodeURIComponent(id)}/decommission`);
  }

  /**
   * The caller's org deployment-count quota (used vs cap). Org-scoped: a platform
   * admin sending X-Impersonate-Org-Id sees the impersonated org's quota.
   */
  getQuota(): Promise<{ quota: DeploymentQuota }> {
    return this.http.get(`${BASE}/quota`);
  }

  /**
   * Platform-admin only: set an org's deployment cap (null → revert to the
   * platform default). Operates on the explicit orgId via the admin pool, so it
   * is independent of impersonation. Returns the refreshed quota.
   */
  setOrgQuota(orgId: string, body: SetDeploymentQuotaRequest): Promise<{ quota: DeploymentQuota }> {
    return this.http.put(`/admin/orgs/${encodeURIComponent(orgId)}/deployment-quota`, { body });
  }
}
